import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import useUserStore from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import DroneHistory from "../DroneFleet/DronePage/DroneHistory";
import { flagDrone } from "../utils/flag-utils";
import { buttonClass } from "../components/ui/Button";
import Dialog from "../components/ui/Dialog";
import Alert from "../components/ui/Alert";
import { Chip } from "../components/ui/Chip";
import { Field } from "../components/ui/Field";

const GoFlagPopup = ({ serialnumber, togglePopup }) => {
  const [droneObj, setDroneObj] = useState(null);
  const [droneKey, setDroneKey] = useState("");
  const [flagDesc, setFlagDesc] = useState("");
  const [editFlag, setEditFlag] = useState(false);
  const [flagNew, setFlagNew] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const api = useApi();
  const toast = useToast();
  const user = useUserStore((state) => state.user);
  const drones = useFleetStore((state) => state.drones);
  const flags = useFleetStore((state) => state.flags);
  const refreshFleet = useFleetStore((state) => state.refreshFleet);

  const categories = [
    "ESC or Motor",
    "LED board",
    "AutoPilot Issues",
    "GPS Issues",
    "LoRa Issues",
    "No Uploads",
    "Hard Landing",
    "Dark Fall",
    "Connection",
    "Fire/Shorts",
    "Vibrations",
    "Check Props",
  ];

  // Reset state and close popup
  const resetPopupState = () => {
    setDroneObj(null);
    setDroneKey("");
    setFlagDesc("");
    setEditFlag(false);
    setFlagNew("");
    togglePopup();
  };

  // Atomic workflow: flags drone in ONE transaction
  const handleSubmit = async () => {
    if (!droneObj || !droneKey) {
      toast.warning("Drone details are missing!");
      return;
    }
    // Dialog's onSubmit ignores the footer button's disabled state, so Enter lands here.
    if (!editFlag || !flagNew.trim()) return;

    setIsSubmitting(true);
    try {
      await flagDrone(api, droneKey, flagNew);
      refreshFleet();
      resetPopupState();
    } catch (error) {
      toast.error("Error flagging drone. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Looks up any existing flag description for the given serial number
  const findDescript = useCallback((drone_serial) => {
    const foundFlag = flags.find((flag) => flag.drone_serial === drone_serial);
    return foundFlag ? foundFlag.descript : null;
  }, [flags]);

  // Finds the drone by partial or full serial number
  const findScannedFlag = useCallback((scanSerial) => {
    const drone = drones.find((d) => d.serialnumber?.endsWith(scanSerial));
    if (!drone) {
      toast.warning("Serial Number not registered!");
      return;
    }

    setDroneObj(drone);
    const finalKey = drone.serialnumber;
    setDroneKey(finalKey);

    const description = findDescript(finalKey);
    if (description) {
      setFlagDesc(description);
      setEditFlag(false);
    } else {
      setFlagDesc(null);
      setEditFlag(true);
    }
  }, [drones, findDescript]);

  useEffect(() => {
    findScannedFlag(serialnumber);
  }, [serialnumber, findScannedFlag]);

  const shortSerial = droneKey ? droneKey : serialnumber;

  return (
    <Dialog
      open
      onClose={resetPopupState}
      label={`Flag ${shortSerial}`}
      title={
        <span className="flex items-center gap-2">
          <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary-ink border border-primary">
            {flagDesc ? "FLAGGED" : "FLAG"}
          </span>
          <span className="font-mono">{shortSerial}</span>
        </span>
      }
      meta={flagDesc || undefined}
      size="md"
      busy={isSubmitting}
      dirty={editFlag && flagNew.trim() !== ""}
      onSubmit={() => handleSubmit()}
      footer={
        <>
          <button
            type="button"
            onClick={resetPopupState}
            className={buttonClass('quiet', 'md')}
          >
            Cancel
          </button>

          {droneKey && (
            <Link to={`/drone/${droneKey}`} onClick={resetPopupState}>
              <button
                type="button"
                className={buttonClass('quiet', 'md')}
              >
                Drone
              </button>
            </Link>
          )}

          {editFlag && (
            <button
              type="submit"
              disabled={isSubmitting || !flagNew.trim()}
              className={buttonClass('filled', 'md')}
            >
              {isSubmitting ? "..." : "Flag"}
            </button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {/* Drone History - compact mode */}
        {droneKey && (
          <DroneHistory drone_serial={droneKey} simpleMode={true} hideTabs={true} />
        )}

        {/* Flag Input Section */}
        {editFlag && (
          <>
            {/* Description Input */}
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">
                Flag Reason
              </label>
              <Field
                type="text"
                value={flagNew}
                onChange={(e) => setFlagNew(e.target.value)}
                placeholder="Enter description or select below..."
                autoFocus
              />
            </div>

            {/* Quick Categories */}
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">
                Quick Select
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {categories.map((category) => {
                  const isSelected = flagNew === category;
                  return (
                    <Chip
                      key={category}
                      pressed={isSelected}
                      onClick={() => setFlagNew(category)}
                      className="w-full justify-center"
                    >
                      {category}
                    </Chip>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Already flagged message */}
        {!editFlag && flagDesc && (
          <Alert tone="warning" variant="note">
            Already flagged. View or edit from the drone page.
          </Alert>
        )}
      </div>
    </Dialog>
  );
};

export default GoFlagPopup;
